import { createFileRoute } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  CAPABILITY_GROUPS,
  CAPABILITIES,
  ROLE_PRESETS,
  WILDCARD,
  hasCapability,
  type Capability,
  type GrantableCapability,
} from "@brewbase/lib";
import type { OperatorId } from "@brewbase/db/types";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RequireCapability } from "@/components/require-capability";
import { OperatorPhoneCell } from "@/features/fermentation-alerts/operator-phone-cell";

export const Route = createFileRoute("/settings/operators")({
  component: () => (
    <RequireCapability cap="operators:manage-capabilities">
      <OperatorsAdmin />
    </RequireCapability>
  ),
});

/** Short column header per capability — the group name gives the rest of the context. */
const SHORT_LABEL: Record<Capability, string> = {
  "harvest-template:edit": "Harvest",
  "test-template:edit": "Test",
  "test-attribute-code:edit": "Analytes",
  "test-attribute-code:publish": "Publish analytes",
  "formula-template:edit": "Formula",
  "sensory-template:edit": "Sensory",
  "harvest-template:correct": "Harvest fix (any)",
  "harvest-template:correct-own": "Harvest fix (own)",
  "test-template:correct": "Test fix (any)",
  "test-template:correct-own": "Test fix (own)",
  "batch-clearance:configure": "Clearance",
  "fermentation-dashboard:edit":
    "Create and edit shared operating dashboards and procedure defaults",
  "cleaning-procedures:edit": "Cleaning",
  "fermentation-registry:edit": "Parameters & lines",
  "inventory:manage": "Inventory",
  "brewcontrol-nodes:manage": "Nodes",
  "brewcontrol-device:override": "Force outputs",
  "brewcontrol-device:calibrate": "Calibrate",
  "device:configure": "Devices",
  "operators:manage-capabilities": "Access",
  "food-taster:erase": "Erase tasters",
};

function OperatorsAdmin() {
  const utils = trpc.useUtils();
  const { data: operators, isLoading } =
    trpc.operator.adminListOperators.useQuery();

  const invalidate = () => utils.operator.adminListOperators.invalidate();
  const grant = trpc.operator.grantCapability.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });
  const revoke = trpc.operator.revokeCapability.useMutation({
    onSuccess: invalidate,
    onError: (err) => toast.error(err.message),
  });

  const setGrant = (
    operatorId: OperatorId,
    capability: GrantableCapability,
    on: boolean,
  ) => {
    if (on) grant.mutate({ operatorId, capability });
    else revoke.mutate({ operatorId, capability });
  };

  const applyPreset = (
    operatorId: OperatorId,
    preset: keyof typeof ROLE_PRESETS,
  ) => {
    for (const cap of ROLE_PRESETS[preset]) {
      grant.mutate({ operatorId, capability: cap });
    }
  };

  if (isLoading || !operators) {
    return <div className="container mx-auto px-4 py-6">Loading…</div>;
  }

  const flatCaps = CAPABILITY_GROUPS.flatMap((g) => g.capabilities);

  return (
    <div className="container mx-auto px-4 py-4">
      <div>
        <h1 className="text-lg font-semibold">Access</h1>
        <p className="text-sm text-muted-foreground">
          Grant capabilities to operators, and set the number live fermentation
          alerts call. A superuser holds every capability, including future
          ones; an operator with no number is never called.
        </p>
      </div>

      <div className="mt-4 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead rowSpan={2} className="align-bottom">
                Operator
              </TableHead>
              <TableHead rowSpan={2} className="align-bottom">
                Alert phone
              </TableHead>
              <TableHead rowSpan={2} className="align-bottom text-center">
                Superuser
              </TableHead>
              {CAPABILITY_GROUPS.map((group) => (
                <TableHead
                  key={group.group}
                  colSpan={group.capabilities.length}
                  className="text-center border-l border-olive-6"
                >
                  {group.group}
                </TableHead>
              ))}
              <TableHead rowSpan={2} className="align-bottom">
                Presets
              </TableHead>
            </TableRow>
            <TableRow>
              {flatCaps.map((cap) => (
                <TableHead
                  key={cap}
                  className="text-center text-xs font-normal"
                  title={CAPABILITIES[cap]}
                >
                  {SHORT_LABEL[cap]}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {operators.map((op) => {
              const isSuperuser = op.capabilities.includes(WILDCARD);
              return (
                <TableRow key={op.id}>
                  <TableCell>
                    <div className="font-medium">
                      {op.full_name ?? op.email ?? `#${op.id}`}
                    </div>
                    {op.full_name && op.email && (
                      <div className="text-xs text-muted-foreground">
                        {op.email}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <OperatorPhoneCell
                      operatorId={op.id}
                      phone={op.phone_e164}
                    />
                  </TableCell>
                  <TableCell className="text-center">
                    <Checkbox
                      checked={isSuperuser}
                      onCheckedChange={(c) =>
                        setGrant(op.id, WILDCARD, c === true)
                      }
                    />
                  </TableCell>
                  {flatCaps.map((cap) => (
                    <TableCell
                      key={cap}
                      className="text-center border-l border-olive-6"
                    >
                      <Checkbox
                        checked={hasCapability(op.capabilities, cap)}
                        disabled={isSuperuser}
                        onCheckedChange={(c) =>
                          setGrant(op.id, cap, c === true)
                        }
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="flex gap-1">
                      {(
                        Object.keys(
                          ROLE_PRESETS,
                        ) as (keyof typeof ROLE_PRESETS)[]
                      ).map((preset) => (
                        <Button
                          key={preset}
                          variant="ghost"
                          size="sm"
                          onClick={() => applyPreset(op.id, preset)}
                        >
                          {preset}
                        </Button>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
